import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Chip, Skeleton } from '@mui/material';
import { green, orange, red, grey } from '@mui/material/colors';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ScheduleIcon from '@mui/icons-material/Schedule';
import api from '../../../services/api';

const fmt = (iso) => {
    if (!iso) return '--';
    const d = new Date(iso);
    return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const untilNext = (iso) => {
    if (!iso) return null;
    const now = new Date();
    const next = new Date(iso);
    const diffMs = next - now;
    if (diffMs <= 0) return 'agora';
    const h = Math.floor(diffMs / 3600000);
    const m = Math.floor((diffMs % 3600000) / 60000);
    return `${h}h${String(m).padStart(2, '0')}`;
};

const colorByPct = (p) => p >= 98 ? green[600] : p >= 90 ? orange[600] : red[600];

export default function CoverageHero() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get('/hermes/dashboard/coverage_today')
            .then(r => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Card><CardContent><Skeleton height={120} /></CardContent></Card>;
    if (!data) return null;

    const pct = data.pct ?? 0;
    const delta = data.delta_pp ?? 0;
    const TrendIcon = delta > 0.5 ? TrendingUpIcon : delta < -0.5 ? TrendingDownIcon : TrendingFlatIcon;
    const trendColor = delta > 0.5 ? green[600] : delta < -0.5 ? red[600] : grey[600];

    return (
        <Card elevation={2}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography variant="overline" color="text.secondary">
                            Cobertura de hoje
                        </Typography>
                        <Box display="flex" alignItems="baseline" gap={1.5}>
                            <Typography variant="h3" fontWeight={700} sx={{ color: colorByPct(pct), fontFamily: 'Montserrat' }}>
                                {pct}%
                            </Typography>
                            <Typography variant="h6" color="text.secondary">
                                {data.covered} / {data.total_active} hosts
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                            <TrendIcon sx={{ color: trendColor, fontSize: 18 }} />
                            <Typography variant="body2" sx={{ color: trendColor }}>
                                {delta > 0 ? '+' : ''}{delta} pp vs ontem
                            </Typography>
                            {data.total_inactive > 0 && (
                                <Chip label={`${data.total_inactive} desativados`} size="small"
                                      sx={{ ml: 1.5, bgcolor: grey[100], color: grey[700] }} />
                            )}
                        </Box>
                    </Box>

                    <Box textAlign="right">
                        <Chip icon={<ScheduleIcon />}
                              label={`Próxima janela em ${untilNext(data.next_cycle_start) || '--'}`}
                              size="small" variant="outlined" />
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                            {fmt(data.next_cycle_start)}
                        </Typography>
                    </Box>
                </Box>

                <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    sx={{
                        mt: 2, height: 10, borderRadius: 5,
                        bgcolor: grey[200],
                        '& .MuiLinearProgress-bar': { bgcolor: colorByPct(pct), borderRadius: 5 },
                    }}
                />
            </CardContent>
        </Card>
    );
}
